import { useEffect, useRef, useState } from 'react';
import { Scene3D } from '../3D/Scene3D';
import { Game } from '../types/game';
import { SceneObject } from '../types';
import { PlayRuntime, GameState } from '../game/runtime';
import api from '../services/api';

// On-screen joystick for touch devices. Reports a normalized -1..1 vector.
function TouchJoystick({ onChange }: { onChange: (x: number, z: number) => void }) {
  const baseRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(false);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const radius = 48;

  const move = (clientX: number, clientY: number) => {
    const base = baseRef.current;
    if (!base) return;
    const rect = base.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = clientX - cx;
    let dy = clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > radius) {
      dx = (dx / dist) * radius;
      dy = (dy / dist) * radius;
    }
    setKnob({ x: dx, y: dy });
    onChange(dx / radius, dy / radius);
  };

  const down = (e: React.PointerEvent) => {
    activeRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    move(e.clientX, e.clientY);
  };
  const drag = (e: React.PointerEvent) => {
    if (activeRef.current) move(e.clientX, e.clientY);
  };
  const up = () => {
    activeRef.current = false;
    setKnob({ x: 0, y: 0 });
    onChange(0, 0);
  };

  return (
    <div
      ref={baseRef}
      onPointerDown={down}
      onPointerMove={drag}
      onPointerUp={up}
      onPointerCancel={up}
      className="absolute bottom-10 left-8 w-28 h-28 rounded-full bg-white/10 border border-white/20 backdrop-blur"
      style={{ touchAction: 'none' }}
    >
      <div
        className="absolute w-12 h-12 rounded-full bg-white/40 pointer-events-none"
        style={{
          left: '50%',
          top: '50%',
          transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))`,
        }}
      />
    </div>
  );
}

function formatTime(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export default function PlayPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<Scene3D | null>(null);
  const runtimeRef = useRef<PlayRuntime | null>(null);
  const [isTouch] = useState(
    () =>
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('Untitled Game');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [game, setGame] = useState<GameState>({
    hasPlayer: false,
    collected: 0,
    totalCollectibles: 0,
    score: 0,
    lives: 0,
    maxLives: 0,
    timeLeft: null,
    won: false,
    lost: false,
    lostReason: null,
  });

  useEffect(() => {
    let scene: Scene3D | null = null;
    let runtime: PlayRuntime | null = null;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const id = params.get('project');
      const fromEditor = params.get('from') === 'editor';
      if (!id) {
        setError('No game specified.');
        setLoading(false);
        return;
      }
      setProjectId(id);

      let objects: SceneObject[] = [];
      let gameTitle = 'Untitled Game';
      try {
        const res = await api.get(`/projects/${id}`);
        const data = res.data?.data;
        const stored = data?.scene;
        if (stored && !Array.isArray(stored) && Array.isArray(stored.scenes)) {
          const g = stored as Game;
          gameTitle = g.title || data?.name || gameTitle;
          objects = (g.scenes?.[0]?.objects ?? []) as SceneObject[];
        } else {
          gameTitle = data?.name || gameTitle;
          objects = Array.isArray(stored) ? stored : [];
        }
      } catch {
        setError('Could not load this game.');
        setLoading(false);
        return;
      }
      setTitle(gameTitle);

      // Count this as a play — but not the creator's own tests launched from
      // the editor. Fire-and-forget; never blocks the game.
      if (!fromEditor) {
        api.post(`/projects/${id}/play`).catch(() => {});
      }
      // Strip the tracking marker so a copied link stays clean and shareable.
      if (params.has('from')) {
        params.delete('from');
        const qs = params.toString();
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${qs ? `?${qs}` : ''}`
        );
      }

      if (canvasRef.current) {
        scene = new Scene3D(canvasRef.current);
        sceneRef.current = scene;
        // Clean experience: drop the editor's grid/axes helpers
        scene.scene.remove(scene.gridHelper);
        scene.scene.remove(scene.axesHelper);
        scene.loadObjects(objects);
        scene.animate();
        runtime = new PlayRuntime(scene, objects, setGame);
        runtime.start();
        runtimeRef.current = runtime;
      }
      setLoading(false);
    };

    run();
    return () => {
      runtime?.dispose();
      scene?.dispose();
    };
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="w-full h-screen relative bg-gray-950">
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ display: 'block' }}
      />

      {/* Top overlay */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 pointer-events-none">
        <div className="text-white font-bold text-lg drop-shadow pointer-events-auto">
          {title}
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            onClick={copyLink}
            className="text-xs text-gray-200 hover:text-white bg-black/40 rounded px-3 py-1.5 backdrop-blur"
          >
            {copied ? 'Link copied' : 'Copy link'}
          </button>
          <a
            href={projectId ? `/editor?project=${projectId}` : '/projects'}
            className="text-xs text-gray-200 hover:text-white bg-black/40 rounded px-3 py-1.5 backdrop-blur"
          >
            Edit in Studio
          </a>
        </div>
      </div>

      {/* Live stats HUD */}
      {!loading && !error && game.hasPlayer && !game.won && !game.lost && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/50 rounded-full px-5 py-2 backdrop-blur pointer-events-none">
          {game.totalCollectibles > 0 && (
            <span className="text-sm font-semibold text-yellow-300">
              ★ {game.score}
            </span>
          )}
          {game.totalCollectibles > 0 && (
            <span className="text-xs text-gray-300">
              {game.collected}/{game.totalCollectibles}
            </span>
          )}
          {game.timeLeft !== null && (
            <span
              className={
                'text-sm font-semibold tabular-nums ' +
                (game.timeLeft <= 10 ? 'text-red-400' : 'text-white')
              }
            >
              ⏱ {formatTime(game.timeLeft)}
            </span>
          )}
          {game.maxLives > 0 && (
            <span className="text-sm text-red-400" aria-label={`${game.lives} lives left`}>
              {'\u2665'.repeat(game.lives)}
              <span className="text-gray-600">
                {'\u2665'.repeat(game.maxLives - game.lives)}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Branding */}
      <a
        href="/"
        className="absolute bottom-3 right-4 text-[11px] text-gray-400 hover:text-white bg-black/40 rounded px-2 py-1 backdrop-blur"
      >
        Made with YIP.fun
      </a>

      {/* In-game hint */}
      {!loading && !error && game.hasPlayer && !game.won && !game.lost && (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-black/50 rounded-full px-4 py-2 backdrop-blur pointer-events-none">
          <span className="text-xs text-gray-200">
            {isTouch ? 'Use the stick to move' : 'Arrow keys / WASD to move'}
          </span>
        </div>
      )}

      {!loading && !error && !game.hasPlayer && (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-black/50 rounded-full px-4 py-2 backdrop-blur text-xs text-gray-300 pointer-events-none">
          Preview — add a{' '}
          <span className="text-gray-100 font-semibold">Player</span> behavior in
          the Studio to make it playable
        </div>
      )}

      {isTouch && !loading && !error && game.hasPlayer && !game.won && !game.lost && (
        <TouchJoystick
          onChange={(x, z) => runtimeRef.current?.setTouchDir(x, z)}
        />
      )}

      {game.won && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="text-center">
            <div className="text-4xl font-extrabold text-white mb-2">
              You win!
            </div>
            {game.totalCollectibles > 0 && (
              <div className="text-gray-300 mb-6">
                Score {game.score} · Collected {game.collected} /{' '}
                {game.totalCollectibles}
              </div>
            )}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-5 py-2.5 rounded-lg"
              >
                Play again
              </button>
              <a
                href={projectId ? `/editor?project=${projectId}` : '/projects'}
                className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-5 py-2.5 rounded-lg"
              >
                Make your own
              </a>
            </div>
          </div>
        </div>
      )}

      {game.lost && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="text-center">
            <div className="text-4xl font-extrabold text-white mb-2">
              Game Over
            </div>
            <div className="text-gray-300 mb-6">
              {game.lostReason === 'time' ? "Time's up!" : 'Out of lives.'}
              {game.totalCollectibles > 0 && ` Score ${game.score}.`}
            </div>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-5 py-2.5 rounded-lg"
              >
                Try again
              </button>
              <a
                href={projectId ? `/editor?project=${projectId}` : '/projects'}
                className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-5 py-2.5 rounded-lg"
              >
                Make your own
              </a>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          Loading…
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}
